// ═══ 말소 정산 불러오기 모달 (대행사 계정 전용) ═══
// GET /tax-invoice-drafts/source-settlements?month=YYYY-MM 으로 정산월 거래처 목록을
// 조회 → 선택한 거래처마다 POST /tax-invoice-drafts (sourceType=cancel_settlement).
// 말소대행은 방향이 반대다 — 공급자 = 대행사(supplier 스냅샷), 공급받는자 = 거래처 회사.
// 그래서 draft 에 supplier 스냅샷을 함께 저장하고, export 시 서버가 그 값을 공급자로 쓴다.
// 앱 대응 화면: erp/app/lib/features/tax_invoice/screens/tax_source_settlements_screen.dart
// 설계: erp/app/docs/tax-hometax-export/plan-v2-data-ux.md §1 / §2.2
import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';

var todayYmd = function () {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
};

var thisMonth = function () {
  return new Date().toISOString().slice(0, 7);
};

var num = function (v) { return (parseInt(v) || 0).toLocaleString(); };

// 정산 행 → draft body (앱 tax_source_settlements_screen.dart _create 와 동일 형태)
// 정산월 ±1 이동 ('YYYY-MM'). 화살표로 앞뒤 달을 훑을 수 있게 (Jin 지시).
var shiftMonth = function (ym, delta) {
  var parts = String(ym || '').split('-');
  var y = parseInt(parts[0], 10);
  var m = parseInt(parts[1], 10);
  if (!y || !m) return ym;
  var idx = y * 12 + (m - 1) + delta;
  var ny = Math.floor(idx / 12);
  var nm = (idx % 12) + 1;
  return ny + '-' + (nm < 10 ? '0' + nm : String(nm));
};

var arrowBtn = { width: 28, height: 30, borderRadius: 6, border: '1px solid #d0d5dd', background: '#fff', fontSize: 15, fontWeight: 700, color: '#475569', cursor: 'pointer', padding: 0, lineHeight: '28px' };

var buildSettlementBody = function (s, supplier, month) {
  var supply = parseInt(s.supplyAmount) || 0;
  var tax = parseInt(s.taxAmount) || 0;
  var body = {
    writeDate: todayYmd(),
    taxType: '\uACFC\uC138',
    // 수수료 청구 방향 — 대금 수령 전 발행이라 '청구'.
    purposeType: '\uCCAD\uAD6C',
    invoiceeCorpNum: String(s.bizNo || '').replace(/-/g, '').trim(),
    invoiceeCorpName: s.companyName || '',
    invoiceeCeoName: '',
    invoiceeAddr: '',
    invoiceeEmail: '',
    supplyCostTotal: supply,
    taxTotal: tax,
    items: [{
      name: '\uB9D0\uC18C\uB300\uD589 \uC218\uC218\uB8CC (' + (s.month || month) + ')',
      spec: '', qty: 1, unitCost: supply, supplyCost: supply, tax: tax
    }],
    sourceType: 'cancel_settlement'
  };
  if (supplier) {
    body.supplier = {
      corpNum: String(supplier.corpNum || '').replace(/-/g, '').trim(),
      name: supplier.name || '',
      ceo: supplier.ceo || '',
      addr: supplier.addr || '',
      bizType: supplier.bizType || '',
      bizItem: supplier.bizItem || '',
      email: supplier.email || ''
    };
  }
  return body;
};

function TaxInvoiceSettlementPickModal({ showToast, onClose, onSaved }) {
  var [month, setMonth] = useState(thisMonth());
  var [rows, setRows] = useState([]);
  var [supplier, setSupplier] = useState(null);
  var [loading, setLoading] = useState(false);
  var [saving, setSaving] = useState(false);
  var [picked, setPicked] = useState([]);

  var load = function (ym) {
    if (!/^\d{4}-\d{2}$/.test(ym || '')) { showToast('YYYY-MM', 'err'); return; }
    setLoading(true);
    setPicked([]);
    authFetch(API + '/tax-invoice-drafts/source-settlements?month=' + encodeURIComponent(ym)).then(function (r) { return r.json(); }).then(function (d) {
      setRows(d.settlements || []);
      setSupplier(d.supplier || null);
      setLoading(false);
    }).catch(function () {
      setLoading(false);
      showToast('\uC815\uC0B0 \uC870\uD68C \uC2E4\uD328', 'err');
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(function () { load(thisMonth()); }, []);

  // 거래처 식별 — companyId 기본, 없으면 사업자번호 폴백
  var rowKey = function (s) { return String(s.companyId || s.bizNo || ''); };
  var isPicked = function (s) {
    return picked.indexOf(rowKey(s)) >= 0;
  };

  var toggle = function (s) {
    var k = rowKey(s);
    setPicked(function (prev) {
      return prev.indexOf(k) >= 0 ? prev.filter(function (x) { return x !== k; }) : prev.concat(k);
    });
  };

  var pickedRows = rows.filter(isPicked);
  var totalAmount = pickedRows.reduce(function (a, s) { return a + (parseInt(s.totalAmount) || 0); }, 0);

  var handleSave = function () {
    if (pickedRows.length === 0) { showToast('\uAC70\uB798\uCC98\uB97C 1\uACF3 \uC774\uC0C1 \uC120\uD0DD\uD574\uC8FC\uC138\uC694', 'err'); return; }
    setSaving(true);
    // 행별 supplier 우선, 없으면 top-level 폴백 (서버 계약)
    pickedRows.reduce(function (chain, s) {
      return chain.then(function () {
        var body = buildSettlementBody(s, s.supplier || supplier, month);
        return authFetch(API + '/tax-invoice-drafts', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
        }).then(function (r) { return r.json(); }).then(function (d) {
          if (!d.success) throw new Error(d.error || '\uC800\uC7A5 \uC2E4\uD328');
        });
      });
    }, Promise.resolve()).then(function () {
      setSaving(false);
      onSaved(pickedRows.length);
    }).catch(function (e) {
      setSaving(false);
      showToast(e.message || '\uC800\uC7A5 \uC2E4\uD328', 'err');
    });
  };

  var th = { padding: '8px 6px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontWeight: 700, color: '#475569' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div className="card" style={{ width: 820, maxWidth: '94vw', maxHeight: '86vh', background: '#fff', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column' }} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 15, margin: 0, color: '#1B3A5C' }}>{'\uB9D0\uC18C \uC815\uC0B0 \uBD88\uB7EC\uC624\uAE30'}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#94a3b8' }}>{'\u2715'}</button>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{'\uC815\uC0B0\uC6D4'}</span>
          <button onClick={function () { var prev = shiftMonth(month, -1); setMonth(prev); load(prev); }}
            title={'\uC774\uC804 \uB2EC'} style={arrowBtn}>{'\u2039'}</button>
          <input
            type="month"
            style={{ padding: '7px 10px', border: '1px solid #d0d5dd', borderRadius: 6, fontSize: 12, outline: 'none' }}
            value={month}
            onChange={function (e) { setMonth(e.target.value); }}
            onKeyDown={function (e) { if (e.key === 'Enter') load(month); }}
          />
          <button onClick={function () { var next = shiftMonth(month, 1); setMonth(next); load(next); }}
            title={'\uB2E4\uC74C \uB2EC'} style={arrowBtn}>{'\u203A'}</button>
          <button onClick={function () { load(month); }} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d0d5dd', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{'\uC870\uD68C'}</button>
        </div>

        <div style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', borderRadius: 6, padding: '8px 12px', marginBottom: 10, lineHeight: 1.6 }}>
          {'\uC120\uD0DD\uD55C \uAC70\uB798\uCC98\uC758 \uB9D0\uC18C\uB300\uD589 \uC218\uC218\uB8CC\uAC00 \uBC1C\uD589 \uBAA9\uB85D\uC5D0 \uC800\uC7A5\uB429\uB2C8\uB2E4. \uACF5\uAE09\uC790(\uB300\uD589\uC0AC) \uC815\uBCF4\uAC00 \uD568\uAED8 \uC800\uC7A5\uB418\uC5B4 \uD648\uD0DD\uC2A4 \uC5D1\uC140\uC5D0 \uADF8\uB300\uB85C \uBC18\uC601\uB429\uB2C8\uB2E4.'}
          <br />
          {'\uC774\uBBF8 \uD31D\uBE4C\uB85C \uBC1C\uD589\uD55C \uC815\uC0B0\uC740 "\uD31D\uBE4C \uBC1C\uD589\uB428" \uC73C\uB85C \uD45C\uC2DC\uB429\uB2C8\uB2E4. \uC911\uBCF5 \uBC1C\uD589\uC5D0 \uC8FC\uC758\uD558\uC138\uC694.'}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 200 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{'\uB85C\uB529 \uC911...'}</div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{'\uD574\uB2F9 \uC6D4\uC758 \uB9D0\uC18C \uC815\uC0B0 \uB0B4\uC5ED\uC774 \uC5C6\uC2B5\uB2C8\uB2E4'}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={Object.assign({}, th, { width: 32 })}></th>
                  {['\uAC70\uB798\uCC98', '\uC0AC\uC5C5\uC790\uBC88\uD638', '\uACF5\uAE09\uAC00\uC561', '\uC138\uC561', '\uD569\uACC4', '\uC0C1\uD0DC'].map(function (h, i) {
                    return <th key={i} style={th}>{h}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map(function (s, idx) {
                  var checked = isPicked(s);
                  return (
                    <tr key={rowKey(s) || idx} onClick={function () { toggle(s); }} style={{ borderBottom: '1px solid #f1f5f9', background: checked ? '#eff6ff' : '#fff', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} readOnly />
                      </td>
                      <td style={{ padding: '8px 6px', fontWeight: 600 }}>{s.companyName || '-'}</td>
                      <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 11 }}>{s.bizNo || '-'}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600 }}>{num(s.supplyAmount)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{num(s.taxAmount)}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600 }}>{num(s.totalAmount)}</td>
                      <td style={{ padding: '8px 6px' }}>
                        {s.alreadyIssued
                          ? <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#dcfce7', color: '#16a34a' }}>{'\uD31D\uBE4C \uBC1C\uD589\uB428'}</span>
                          : <span style={{ fontSize: 10, color: '#94a3b8' }}>{'-'}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 12, color: '#475569' }}>
            {'\uC120\uD0DD '}<b>{pickedRows.length}</b>{'\uACF3 \u00B7 \uD569\uACC4 '}<b>{totalAmount.toLocaleString()}</b>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{'\uCDE8\uC18C'}</button>
            <button onClick={handleSave} disabled={saving || pickedRows.length === 0} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: saving || pickedRows.length === 0 ? '#94a3b8' : '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving || pickedRows.length === 0 ? 'default' : 'pointer' }}>
              {saving ? '\uC800\uC7A5 \uC911...' : '\uBC1C\uD589 \uBAA9\uB85D\uC5D0 \uC800\uC7A5'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { buildSettlementBody };
export default TaxInvoiceSettlementPickModal;
